import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store/store';
import { fetchPatientProfile } from '../../../store/reducers/authReducer';
import { useFormValidator } from '../../../hooks/useFormValidator';
import { PersonalDetailsSchema, PersonalDetailsInput } from './schemas';
import FloatingInput from '../../../components/FloatingInput';
import PhoneInputField from '../../../components/PhoneInputField';
import { FloatingLabelSelect } from '../../../components/ui';
import DatePickerField from '../../../components/ui/DatePickerField';
import { usePincodeAutoFill } from '../../../hooks/usePincodeAutoFill';
import { toast } from 'react-toastify';
import ApiServices from '../../../shared/Services/apiService';
import {
  AlertCircle, User, MapPin, Phone, CheckCircle2, Loader2,
} from 'lucide-react';
import { useThemeMode } from '../../../theme/ThemeProvider';
import { getCountries } from 'react-phone-number-input';
import INDIAN_STATES from '../../../config/indianStates.json';
import { getIndianCitiesByState } from '../../../config/indianCities';
import CountrySelectField from '../../../components/CountrySelectField';
import { ISO_TO_COUNTRY } from '../../../config/countryConfig';
import { useRegion } from '../../../contexts/RegionContext/RegionContext';

const _regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const NAME_TO_ISO: Record<string, string> = Object.fromEntries(
  getCountries().map(code => [_regionNames.of(code) || code, code])
);

const parseAddress = (addr: any): Partial<PersonalDetailsInput> => {
  if (!addr) return {};
  if (typeof addr === 'object') {
    return {
      addressLine1: addr.AddressLine1 || '',
      addressLine2: addr.AddressLine2 || '',
      city: addr.City || '',
      state: addr.State || '',
      country: addr.Country || '',
      zipCode: addr.ZipCode || '',
    };
  }
  if (typeof addr === 'string' && addr.includes(' | ')) {
    const [addressLine1 = '', addressLine2 = '', city = '', state = '', country = '', zipCode = ''] = addr.split(' | ');
    return { addressLine1, addressLine2, city, state, country, zipCode };
  }
  return { addressLine1: String(addr) };
};

const BLANK: PersonalDetailsInput = {
  firstName: '', middleName: '', lastName: '', dob: '', phone: '',
  gender: undefined as any, email: '', addressLine1: '', addressLine2: '',
  city: '', state: '', country: '', zipCode: '', PK: '',
};

const Section = ({
  icon, title, children, isDark
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  isDark: boolean;
}) => (
  <div className={[
    'rounded-2xl border p-4 sm:p-6 space-y-4 sm:space-y-5',
    isDark
      ? 'bg-surface-dark-300 border-dark-border-DEFAULT shadow-dark-card'
      : 'bg-white border-surface-200 shadow-elevation-2',
  ].join(' ')}>
    {/* Section header */}
    <div className="flex items-center gap-2.5">
      <span className={[
        'w-8 h-8 rounded-xl flex items-center justify-center ring-1 ring-inset flex-shrink-0',
        isDark
          ? 'bg-brand-500/15 ring-brand-500/20 text-brand-300'
          : 'bg-brand-50 ring-brand-500/15 text-brand-600',
      ].join(' ')}>
        {icon}
      </span>
      <h4 className={`text-sm font-bold tracking-tight ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
        {title}
      </h4>
    </div>
    {children}
  </div>
);

const NativeField = ({
  label, required: req, error, children, isDark
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  isDark: boolean;
}) => (
  <div className="w-full">
    <label className={`block mb-1.5 text-nav-xs font-semibold tracking-wide ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
      {label}
      {req && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && (
      <p className={`mt-1.5 flex items-center gap-1.5 text-nav-xs text-red-500 dark:text-red-400`}>
        <AlertCircle size={13} />
        {error}
      </p>
    )}
  </div>
);

type Props = {
  initialData?: Record<string, any>;
  onComplete: (data: any) => void;
  triggerSubmit?: (fn: () => void) => void;
  setLoading: (loading: boolean) => void;
  selectedCountry?: string;
  lockPhoneCountry?: boolean;
};

const PersonalDetails: React.FC<Props> = ({ initialData, onComplete, triggerSubmit, setLoading, selectedCountry, lockPhoneCountry: lockProp = false }) => {

  const { isDark } = useThemeMode();
  const { isIndia } = useRegion();
  const lockPhoneCountry = isIndia ? lockProp : false;
  const profile = useSelector((s: RootState) => s.auth.profile);
  const dispatch = useDispatch<any>();
  const hasInit = useRef(false);

  useEffect(() => {
    hasInit.current = false;
    dispatch(fetchPatientProfile());
  }, []);

  const { formData, setFormData, handleChange, validate, errors, getLastParsed } =
    useFormValidator<PersonalDetailsInput>(BLANK, PersonalDetailsSchema);

  const {
    status: pinStatus,
    resolvedCity,
    resolvedState,
    onPincodeChange,
    reset: resetPincode,
  } = usePincodeAutoFill();

  // Apply resolved city+state when lookup succeeds
  useEffect(() => {
    if (pinStatus === 'success' && resolvedCity) handleChange('city', resolvedCity);
  }, [resolvedCity, pinStatus]);
  useEffect(() => {
    if (pinStatus === 'success' && resolvedState) handleChange('state', resolvedState);
  }, [resolvedState, pinStatus]);

  useEffect(() => {
    if (hasInit.current) return;
    if (initialData && Object.keys(initialData).length > 0) {
      const ap = parseAddress(initialData.address || '');
      setFormData({
        firstName: initialData.firstName || '', middleName: initialData.middleName || '',
        lastName: initialData.lastName || '', dob: initialData.dob || '',
        phone: initialData.phone || '', gender: initialData.gender || '',
        email: initialData.email || '',
        addressLine1: initialData.addressLine1 || ap.addressLine1 || '',
        addressLine2: initialData.addressLine2 || ap.addressLine2 || '',
        city: initialData.city || ap.city || '', state: initialData.state || ap.state || '',
        country: initialData.country || selectedCountry || ap.country || '',
        zipCode: initialData.zipCode || ap.zipCode || '', PK: initialData.PK || '',
      });
      hasInit.current = true;
    } else if (profile) {
      const ap = parseAddress(profile?.Address || '');
      setFormData({
        firstName: profile?.FirstName || '',
        middleName: profile?.MiddleName || '',
        lastName: profile?.LastName || '',
        dob: profile?.DateOfBirth || '',
        phone: (profile?.Phone || '').replace(/\s+/g, ''),
        gender: profile?.Gender || '',
        email: profile?.Email || '',
        addressLine1: ap.addressLine1 || '', addressLine2: ap.addressLine2 || '',
        city: ap.city || '', state: ap.state || '',
        country: selectedCountry || ap.country || '', zipCode: ap.zipCode || '',
        PK: profile?.PK || '',
      });
      hasInit.current = true;
    }
  }, [profile, initialData, selectedCountry]);

  useEffect(() => {
    if (selectedCountry && hasInit.current) {
      handleChange('country', selectedCountry);
      resetPincode();
    }
  }, [selectedCountry]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    const parsed = getLastParsed() || formData;

    setLoading(true);

    const profile = {
      FirstName: parsed.firstName,
      MiddleName: parsed.middleName,
      LastName: parsed.lastName,
      Phone: parsed.phone,
      DateOfBirth: parsed.dob,
      Gender: parsed.gender,
      Email: parsed.email,
      Address: {
        AddressLine1: parsed.addressLine1,
        AddressLine2: parsed.addressLine2,
        City: parsed.city,
        State: parsed.state,
        ZipCode: parsed.zipCode,
      },
      Country: parsed.country,
      PK: parsed.PK,
    };

    try {
      // Details only. `onboarding_completed` is deliberately NOT sent here —
      // this is step 2 of 3, and a patient who saves their details but never
      // picks a service has not finished onboarding. Marking it complete here
      // let them skip the whole stepper on the next sign-in without ever
      // choosing a service. The flag is written when step 3 completes.
      await ApiServices.updatePatientProfile(profile);

      toast.success('Personal Details saved');
      onComplete({ personalDetails: { ...parsed } });
    } catch { toast.error('Error saving personal details'); }
    finally { setLoading(false); }
  }, [formData, validate, getLastParsed, onComplete, setLoading]);

  const submitRef = useRef(handleSubmit);
  const hasReg = useRef(false);
  useEffect(() => { submitRef.current = handleSubmit; }, [handleSubmit]);

  useEffect(() => {
    if (triggerSubmit && !hasReg.current) {
      triggerSubmit(() => submitRef.current());
      hasReg.current = true;
    }
  }, [triggerSubmit]);

  return (
    <div className="w-full mx-auto space-y-4">

      {/* ── Section 1: Full Name ── */}
      <Section icon={<User size={15} />} title="Full Name" isDark={isDark}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
          <div className="sm:col-span-1 md:col-span-1 lg:col-span-4">
            <FloatingInput
              id="first-name-input"
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              error={errors.firstName}
              required
            />
          </div>
          <div className="sm:col-span-1 md:col-span-1 lg:col-span-4">
            <FloatingInput
              id="middle-name-input"
              label="Middle Name"
              value={formData.middleName ?? ''}
              onChange={(e) => handleChange('middleName', e.target.value)}
              error={errors.middleName}
            />
          </div>
          <div className="sm:col-span-2 md:col-span-1 lg:col-span-4">
            <FloatingInput
              id="last-name-input"
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              error={errors.lastName}
              required
            />
          </div>
        </div>
      </Section>

      {/* ── Section 2: Contact Information ── */}
      <Section icon={<Phone size={15} />} title="Contact Information" isDark={isDark}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
          <div className="sm:col-span-1 md:col-span-1 lg:col-span-4">
            <DatePickerField
              label="Date of Birth"
              required
              value={formData.dob || ''}
              onChange={(v) => handleChange('dob', v)}
              error={errors.dob}
            />
          </div>

          <div className="sm:col-span-1 md:col-span-1 lg:col-span-4">
            <FloatingLabelSelect
              label="Gender"
              required
              value={formData.gender || ''}
              onChange={(v) => handleChange('gender', v)}
              error={errors.gender}
              options={[
                { value: 'Male', label: 'Male' },
                { value: 'Female', label: 'Female' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </div>


          <div className="sm:col-span-2 md:col-span-1 lg:col-span-4">
            <NativeField label="Phone" required error={errors.phone} isDark={isDark}>
              <PhoneInputField
                variant="standard"
                value={formData.phone}
                onChange={(val) => handleChange('phone', val)}
                required
                defaultCountry={(formData.country ? NAME_TO_ISO[formData.country] : undefined) || (selectedCountry ? NAME_TO_ISO[selectedCountry] : undefined) as any}
                lockCountry={lockPhoneCountry}
              />
            </NativeField>
          </div>

          <div className="sm:col-span-2 md:col-span-3 lg:col-span-12 lg:mt-1">
            <FloatingInput
              id="email-input"
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              required
              readOnly
            />
          </div>
        </div>
      </Section>

      {/* ── Section 3: Address ── */}
      <Section icon={<MapPin size={15} />} title="Address" isDark={isDark}>
        <div className="space-y-4">

          {/* ── Row 1: Street ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-5 lg:gap-6">
            <div className="sm:col-span-1 lg:col-span-6">
              <FloatingInput
                id="address-line1-input"
                label="Address Line 1"
                value={formData.addressLine1}
                onChange={(e) => handleChange('addressLine1', e.target.value)}
                error={errors.addressLine1}
                required
              />
            </div>
            <div className="sm:col-span-1 lg:col-span-6">
              <FloatingInput
                id="address-line2-input"
                label="Address Line 2 (Optional)"
                value={formData.addressLine2}
                onChange={(e) => handleChange('addressLine2', e.target.value)}
                error={errors.addressLine2}
              />
            </div>
          </div>

          {/* ── Row 2: Location (Country, PIN, State, City) ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-5 lg:gap-6 items-end">
            <div className="sm:col-span-1 lg:col-span-3">
              {lockPhoneCountry ? (
                <FloatingInput
                  id="country-input"
                  label="Country"
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  error={errors.country}
                  required
                  readOnly
                />
              ) : (
                <CountrySelectField
                  label="Country"
                  required
                  value={NAME_TO_ISO[formData.country || ''] ?? formData.country}
                  onChange={(iso) => {
                    const fullName = ISO_TO_COUNTRY[iso] ?? iso;
                    handleChange('country', fullName);
                    resetPincode();
                  }}
                  error={errors.country}
                />
              )}
            </div>

            <div className="sm:col-span-1 lg:col-span-3">
              <div className="relative">
                <FloatingInput
                  id="zip-input"
                  label={lockPhoneCountry ? 'PIN Code' : formData.country === 'United States' ? 'ZIP Code' : 'Postal Code'}
                  value={formData.zipCode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, lockPhoneCountry ? 6 : 10);
                    handleChange('zipCode', v);
                    onPincodeChange(v, lockPhoneCountry ? 'IN' : NAME_TO_ISO[formData.country || ''] || 'IN');
                  }}
                  error={errors.zipCode}
                  required
                />
                {pinStatus === 'loading' && (
                  <Loader2 size={13} className="absolute right-3 top-1/2 translate-y-1 text-brand-400 animate-spin" />
                )}
                {pinStatus === 'success' && (
                  <CheckCircle2 size={13} className="absolute right-3 top-1/2 translate-y-1 text-emerald-500" />
                )}
                {pinStatus === 'not_found' && (
                  <span className="absolute right-3 top-full text-[10px] text-amber-500 font-medium">Not found</span>
                )}
              </div>
            </div>

            <div className="sm:col-span-1 lg:col-span-3">
              {lockPhoneCountry ? (
                <FloatingLabelSelect
                  label="State"
                  value={formData.state}
                  onChange={(v) => handleChange('state', v)}
                  options={INDIAN_STATES.map((s) => ({ value: s, label: s }))}
                  error={errors.state}
                  required
                />
              ) : (
                <FloatingInput
                  id="state-input"
                  label={
                    formData.country === 'United States' ? 'State (e.g. CA, TX)' :
                      formData.country === 'Canada' ? 'Province (e.g. ON, BC)' :
                        'State / Province'
                  }
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  error={errors.state}
                  required
                />
              )}
            </div>

            <div className="sm:col-span-1 lg:col-span-3">
              {lockPhoneCountry ? (() => {
                const baseCities = getIndianCitiesByState(formData.state).map((c) => ({ value: c, label: c }));
                const cityOptions = formData.city && !baseCities.some((o) => o.value === formData.city)
                  ? [{ value: formData.city, label: formData.city }, ...baseCities]
                  : baseCities;
                return (
                  <FloatingLabelSelect
                    label="City"
                    value={formData.city}
                    onChange={(v) => handleChange('city', v)}
                    options={cityOptions}
                    error={errors.city}
                    required
                  />
                );
              })() : (
                <FloatingInput
                  id="city-input"
                  label="City"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  error={errors.city}
                  required
                />
              )}
            </div>
          </div>

        </div>
      </Section>
    </div>
  );
};

export default PersonalDetails;
